import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { settingsService } from '../../services/dataService';
import { useAuth } from '../../hooks/useAuth';
import { hasPermission } from '../../utils/permissions';
import pageStyles from './SettingsErrorLogsPage.module.css';
import styles from './SettingsPublicAccessPage.module.css';

const MIN_LENGTH = 4;
const PUBLIC_PATH = '/equipment-usage/public';

const statusText = (status) => {
  if (!status) return '';
  if (status.source === 'database') {
    const when = status.updated_at ? new Date(status.updated_at).toLocaleString() : null;
    return ['Access code set', when ? `on ${when}` : null, status.updated_by ? `by ${status.updated_by}` : null]
      .filter(Boolean).join(' ');
  }
  if (status.source === 'environment') {
    return 'Using the code from the server configuration (PUBLIC_USAGE_ACCESS_CODES). Set one here to replace it.';
  }
  return 'No access code is set, so the public page is closed.';
};

const SettingsPublicAccessPage = () => {
  const { user } = useAuth();
  const canWrite = hasPermission(user, 'public_access', 'write');
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      setStatus(await settingsService.getPublicUsageAccess());
    } catch (err) {
      setError(err?.detail || 'Failed to load the access code status');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (event) => {
    event.preventDefault();
    setMessage('');
    const trimmed = code.trim();
    if (trimmed.length < MIN_LENGTH) {
      setError(`The access code must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (trimmed !== confirm.trim()) {
      setError('The two entries do not match.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      setStatus(await settingsService.setPublicUsageAccess(trimmed));
      setCode('');
      setConfirm('');
      setMessage('Access code saved. Share it with the people who should open the public page; anyone using the old code is locked out.');
    } catch (err) {
      setError(err?.detail || 'Failed to save the access code');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the access code? The public page falls back to the server configuration, or closes if none is set.')) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      setStatus(await settingsService.clearPublicUsageAccess());
      setMessage('Access code removed.');
    } catch (err) {
      setError(err?.detail || 'Failed to remove the access code');
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = `${window.location.origin}${PUBLIC_PATH}`;

  return (
    <div className={pageStyles.page}>
      <header className={pageStyles.header}>
        <div>
          <h1>Public Usage Access</h1>
          <p>The access code people enter to open the public equipment usage page without a login.</p>
        </div>
        <Link className={pageStyles.backLink} to="/settings">Back to Settings</Link>
      </header>

      {error ? <div className={pageStyles.error}>{error}</div> : null}
      {message ? <div className={styles.success}>{message}</div> : null}

      <section className={pageStyles.panel}>
        <h2 className={styles.heading}>Status</h2>
        <p className={styles.status}>
          <span className={`${styles.dot} ${status?.source === 'none' ? styles.dotOff : styles.dotOn}`} aria-hidden="true" />
          {status ? statusText(status) : 'Loading…'}
        </p>
        <p className={styles.hint}>
          Public page: <a href={publicUrl} target="_blank" rel="noreferrer">{publicUrl}</a>
        </p>
        <p className={styles.hint}>The code is stored hashed, so it cannot be shown here. To change it, set a new one.</p>
      </section>

      {canWrite ? (
        <section className={pageStyles.panel}>
          <h2 className={styles.heading}>{status?.source === 'database' ? 'Change access code' : 'Set access code'}</h2>
          <form className={styles.form} onSubmit={save}>
            <label>
              New access code
              <input
                type={showCode ? 'text' : 'password'}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="new-password"
                minLength={MIN_LENGTH}
                maxLength={128}
                required
              />
            </label>
            <label>
              Repeat access code
              <input
                type={showCode ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                maxLength={128}
                required
              />
            </label>
            <label className={styles.checkbox}>
              <input type="checkbox" checked={showCode} onChange={(e) => setShowCode(e.target.checked)} />
              Show
            </label>
            <div className={styles.actions}>
              <button type="submit" className={styles.primaryButton} disabled={saving}>
                {saving ? 'Saving…' : 'Save access code'}
              </button>
              {status?.source === 'database' ? (
                <button type="button" className={styles.dangerButton} onClick={remove} disabled={saving}>
                  Remove access code
                </button>
              ) : null}
            </div>
          </form>
        </section>
      ) : null}
    </div>
  );
};

export default SettingsPublicAccessPage;
